import { createBrowserRouter, type RouteObject } from 'react-router-dom'
import type { ReactNode } from 'react'
import { AppShell } from './components/layout/AppShell'
import { DashboardPage } from './features/dashboard/DashboardPage'
import { ContentListPage } from './features/content/ContentListPage'
import { ReservationsPage } from './features/reservations/ReservationsPage'
import { MembersPage } from './features/members/MembersPage'
import { AvailabilityPage } from './features/availability/AvailabilityPage'
import { InOutPage } from './features/inout/InOutPage'
import { RatesPage } from './features/rates/RatesPage'
import { RateCalendarPage } from './features/rates/RateCalendarPage'
import { ConventionsPage } from './features/conventions/ConventionsPage'
import { ConventionConfigPage } from './features/conventions/ConventionConfigPage'
import { CouponsPage } from './features/coupons/CouponsPage'
import { EmailsPage } from './features/templates/EmailsPage'
import { ContractsPage } from './features/templates/ContractsPage'
import { GeneralConfigPage } from './features/config/GeneralConfigPage'
import { TransportsPage } from './features/config/TransportsPage'
import { ServicesPage } from './features/config/ServicesPage'
import { DurationsPage } from './features/config/DurationsPage'
import { PaymentModesPage } from './features/config/PaymentModesPage'
import { PcardConfigPage } from './features/config/PcardConfigPage'
import { ThirdPartyConfigPage } from './features/config/ThirdPartyConfigPage'
import { LogPage } from './features/config/LogPage'
import { PlaceholderPage } from './features/PlaceholderPage'
import { NAV, isGroup, isLabel, type NavItem } from './lib/nav'

// Bespoke pages by route. Anything not listed falls back to the generic content list
// (when the nav leaf maps to a Yep model) or a placeholder.
const PAGES: Record<string, ReactNode> = {
  '/reservations': <ReservationsPage />,
  '/members': <MembersPage />,
  '/availability': <AvailabilityPage />,
  '/in-out': <InOutPage />,
  '/rates': <RatesPage />,
  '/rate-calendar': <RateCalendarPage />,
  '/coupons': <CouponsPage />,
  '/conventions': <ConventionsPage />,
  '/convention-config': <ConventionConfigPage />,
  '/emails': <EmailsPage />,
  '/contracts': <ContractsPage />,
  '/config/general': <GeneralConfigPage />,
  '/config/transports': <TransportsPage />,
  '/config/services': <ServicesPage />,
  '/config/durations': <DurationsPage />,
  '/config/payment-modes': <PaymentModesPage />,
  '/config/pcard': <PcardConfigPage />,
  '/config/third-party': <ThirdPartyConfigPage />,
  '/config/log': <LogPage />,
}

// Collect every leaf from the nav tree (path + whether it maps to a Yep content model).
const leaves: NavItem[] = []
for (const e of NAV) {
  if (isGroup(e)) e.items.forEach((i) => leaves.push(i))
  else if (!isLabel(e)) leaves.push(e)
}

const childRoutes: RouteObject[] = [
  { index: true, element: <DashboardPage /> },
  ...leaves
    .filter((l) => l.to !== '/')
    .map((l) => ({
      path: l.to.replace(/^\//, ''),
      element: PAGES[l.to] ?? (l.model ? <ContentListPage /> : <PlaceholderPage />),
    })),
]

export const router = createBrowserRouter([
  { path: '/', element: <AppShell />, children: childRoutes },
  { path: '*', element: <AppShell />, children: [{ path: '*', element: <PlaceholderPage /> }] },
])
